import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { SEO } from '../components/SEO/SEO';
import { brandColors } from '../theme';

export const EULAPage: React.FC = () => {
  return (
    <>
      <SEO
        title="End User License Agreement"
        description="HTL MarketCompass End User License Agreement (EULA). Review the terms and conditions for using our hotel market intelligence platform."
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 3, md: 6 } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              color: brandColors.indigo,
              mb: 1,
            }}
          >
            End User License Agreement
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                IMPORTANT - READ CAREFULLY
              </Typography>
              <Typography variant="body1" paragraph>
                This End User License Agreement ("Agreement") is a legal agreement between you (either an individual 
                or a single entity, "Customer," "you," or "your") and Barrett HTL Inc ("Company," "we," "us," or "our") 
                for the purchase, access, and use of digital market intelligence reports, data products, and related 
                content available through the HTL MarketCompass Store (collectively, the "Digital Products").
              </Typography>
              <Typography variant="body1" paragraph>
                BY PURCHASING, DOWNLOADING, ACCESSING, OR USING ANY DIGITAL PRODUCTS, YOU ACKNOWLEDGE THAT YOU HAVE 
                READ THIS AGREEMENT, UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS. IF YOU DO NOT AGREE TO THE 
                TERMS OF THIS AGREEMENT, DO NOT PURCHASE, DOWNLOAD, ACCESS, OR USE THE DIGITAL PRODUCTS.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                1. Grant of License
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                1.1 License Grant
              </Typography>
              <Typography variant="body1" paragraph>
                Subject to your compliance with this Agreement and payment of applicable fees, we grant you a limited, 
                non-exclusive, non-transferable, revocable license to access and use the Digital Products solely for 
                your internal business purposes.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                1.2 License Types
              </Typography>
              <Typography variant="body1" paragraph>
                Depending on your purchase, you receive one of the following license types:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  <strong>Single Report License:</strong> One-time access to download and use a specific report
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Subscription License:</strong> Ongoing access to designated reports during the subscription period
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Enterprise License:</strong> Extended usage rights for multiple users within your organization
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Custom Report License:</strong> Tailored access rights as specified in your purchase agreement
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                1.3 Authorized Users
              </Typography>
              <Typography variant="body1" paragraph>
                Unless otherwise specified in your license type:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  Individual licenses are for single-user access only
                </Typography>
                <Typography component="li" variant="body1">
                  Enterprise licenses may be shared within your organization as specified
                </Typography>
                <Typography component="li" variant="body1">
                  You are responsible for all use of Digital Products under your account
                </Typography>
                <Typography component="li" variant="body1">
                  Sharing login credentials is strictly prohibited
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                2. Permitted Uses
              </Typography>
              <Typography variant="body1" paragraph>
                You may use the Digital Products to:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  Conduct market research and analysis for your business decisions
                </Typography>
                <Typography component="li" variant="body1">
                  Create internal presentations and reports incorporating the data
                </Typography>
                <Typography component="li" variant="body1">
                  Share insights internally within your organization (subject to license type)
                </Typography>
                <Typography component="li" variant="body1">
                  Make business and investment decisions based on the information
                </Typography>
                <Typography component="li" variant="body1">
                  Store copies for backup and archival purposes
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                3. Restrictions and Prohibited Uses
              </Typography>
              <Typography variant="body1" paragraph>
                You agree NOT to:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  Resell, redistribute, or sublicense the Digital Products to third parties
                </Typography>
                <Typography component="li" variant="body1">
                  Share Digital Products with competitors or unauthorized parties
                </Typography>
                <Typography component="li" variant="body1">
                  Publish or publicly display substantial portions of the Digital Products
                </Typography>
                <Typography component="li" variant="body1">
                  Use the Digital Products to create competing products or services
                </Typography>
                <Typography component="li" variant="body1">
                  Remove, alter, or obscure any copyright notices or watermarks
                </Typography>
                <Typography component="li" variant="body1">
                  Reverse engineer, decompile, or extract data through automated means
                </Typography>
                <Typography component="li" variant="body1">
                  Use the Digital Products for any unlawful or unauthorized purpose
                </Typography>
                <Typography component="li" variant="body1">
                  Exceed the scope of your purchased license type
                </Typography>
                <Typography component="li" variant="body1">
                  Circumvent any access controls or security measures
                </Typography>
                <Typography component="li" variant="body1">
                  Misrepresent the source or ownership of the Digital Products
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                4. Intellectual Property Rights
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                4.1 Ownership
              </Typography>
              <Typography variant="body1" paragraph>
                All Digital Products, including but not limited to reports, data, analyses, graphics, and methodologies, 
                are and remain the exclusive property of Barrett HTL Inc and its licensors. This Agreement does not 
                transfer any ownership rights to you.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                4.2 Copyright and Trademarks
              </Typography>
              <Typography variant="body1" paragraph>
                The Digital Products are protected by copyright laws and international treaties. All trademarks, 
                service marks, and trade names are owned by Barrett HTL Inc or its licensors.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                4.3 Attribution
              </Typography>
              <Typography variant="body1" paragraph>
                When citing or referencing Digital Products in permitted uses, you must provide appropriate attribution 
                to "HTL MarketCompass" or as otherwise specified in the Digital Products.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                5. Payment Terms
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                5.1 Fees and Payment
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to pay all fees associated with your purchase of Digital Products. All payments are processed 
                securely through our third-party payment processors. Prices are subject to change with notice.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                5.2 Subscriptions
              </Typography>
              <Typography variant="body1" paragraph>
                Subscription fees are billed in advance on a recurring basis (monthly or annually). Subscriptions 
                automatically renew unless cancelled before the renewal date. You may cancel subscriptions through 
                your account settings.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                5.3 Refund Policy
              </Typography>
              <Typography variant="body1" paragraph>
                Due to the digital nature of our products, all sales are final. Refunds may be provided at our sole 
                discretion for technical issues preventing access to purchased Digital Products. Subscription refunds 
                are subject to our subscription terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                6. Digital Product Delivery and Access
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                6.1 Delivery
              </Typography>
              <Typography variant="body1" paragraph>
                Digital Products are delivered electronically via download links or through your account dashboard. 
                Access is typically provided immediately upon payment confirmation.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                6.2 Technical Requirements
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for ensuring you have the necessary hardware, software, and internet connection 
                to access and use the Digital Products. We recommend using current versions of major browsers and 
                PDF readers.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                6.3 Download Limits
              </Typography>
              <Typography variant="body1" paragraph>
                Single report purchases may have download limits. Subscription products provide unlimited access 
                during the subscription period. We reserve the right to restrict excessive downloading that appears 
                abusive or automated.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                7. Data Accuracy and Disclaimers
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                7.1 Information Disclaimer
              </Typography>
              <Typography variant="body1" paragraph>
                While we strive for accuracy, the Digital Products are provided for informational purposes only. 
                Market data and analyses are based on available information at the time of publication and may 
                contain estimates, projections, and forward-looking statements.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                7.2 No Investment Advice
              </Typography>
              <Typography variant="body1" paragraph>
                The Digital Products do not constitute investment, financial, legal, or tax advice. You should 
                consult appropriate professionals before making any business or investment decisions based on 
                the information provided.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                7.3 Updates and Corrections
              </Typography>
              <Typography variant="body1" paragraph>
                We may update or correct Digital Products after publication. Significant updates will be 
                communicated to purchasers when feasible. Historical reports reflect information available 
                at the time of original publication.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                8. Confidentiality
              </Typography>
              <Typography variant="body1" paragraph>
                You acknowledge that the Digital Products contain proprietary and confidential information. 
                You agree to maintain the confidentiality of the Digital Products and not disclose them to 
                unauthorized parties. This obligation survives termination of this Agreement.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                9. Account Security
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  Maintaining the confidentiality of your account credentials
                </Typography>
                <Typography component="li" variant="body1">
                  All activities that occur under your account
                </Typography>
                <Typography component="li" variant="body1">
                  Immediately notifying us of any unauthorized use
                </Typography>
                <Typography component="li" variant="body1">
                  Ensuring your account information is accurate and current
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                10. Warranties and Disclaimers
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                10.1 Limited Warranty
              </Typography>
              <Typography variant="body1" paragraph>
                We warrant that the Digital Products will be provided substantially as described at the time 
                of purchase. This warranty is limited to technical delivery and access issues.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                10.2 Disclaimer of Warranties
              </Typography>
              <Typography variant="body1" paragraph>
                EXCEPT AS EXPRESSLY PROVIDED HEREIN, THE DIGITAL PRODUCTS ARE PROVIDED "AS IS" WITHOUT WARRANTY 
                OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE 
                IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT.
              </Typography>
              <Typography variant="body1" paragraph>
                WE DO NOT WARRANT THAT THE DIGITAL PRODUCTS WILL MEET YOUR SPECIFIC REQUIREMENTS OR THAT ACCESS 
                WILL BE UNINTERRUPTED OR ERROR-FREE.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                11. Indemnification
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to indemnify, defend, and hold harmless Barrett HTL Inc, its affiliates, officers, 
                directors, employees, and agents from and against any claims, liabilities, damages, losses, 
                and expenses, including reasonable attorneys' fees, arising out of or in connection with:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Your use or misuse of the Digital Products</Typography>
                <Typography component="li" variant="body1">Your violation of this Agreement</Typography>
                <Typography component="li" variant="body1">Your violation of any third-party rights</Typography>
                <Typography component="li" variant="body1">Any unauthorized sharing or distribution of Digital Products</Typography>
                <Typography component="li" variant="body1">Your use of Digital Products for prohibited purposes</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                12. Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BARRETT HTL INC BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT 
                LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF 
                OR RELATED TO YOUR USE OF THE DIGITAL PRODUCTS.
              </Typography>
              <Typography variant="body1" paragraph>
                IN NO EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE AMOUNT PAID BY YOU FOR THE SPECIFIC 
                DIGITAL PRODUCT GIVING RISE TO THE CLAIM IN THE TWELVE (12) MONTHS PRECEDING THE EVENT.
              </Typography>
              <Typography variant="body1" paragraph>
                SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES, SO SOME OF 
                THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                13. Term and Termination
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                13.1 Term
              </Typography>
              <Typography variant="body1" paragraph>
                This Agreement is effective upon your purchase or first access to Digital Products and continues 
                until terminated. For subscriptions, the term corresponds to your subscription period.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                13.2 Termination for Breach
              </Typography>
              <Typography variant="body1" paragraph>
                We may immediately terminate or suspend your access to Digital Products if you breach this 
                Agreement. Such termination does not entitle you to any refund.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                13.3 Effect of Termination
              </Typography>
              <Typography variant="body1" paragraph>
                Upon termination:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Your license to use Digital Products immediately ceases</Typography>
                <Typography component="li" variant="body1">You must destroy all copies of Digital Products in your possession</Typography>
                <Typography component="li" variant="body1">Provisions regarding intellectual property, confidentiality, and liability survive</Typography>
                <Typography component="li" variant="body1">No refunds will be provided for termination due to breach</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                14. Export Compliance
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to comply with all applicable export and re-export control laws and regulations. 
                You represent that you are not located in a country subject to U.S. trade embargo and are 
                not on any prohibited party lists.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                15. Governing Law and Dispute Resolution
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                15.1 Governing Law
              </Typography>
              <Typography variant="body1" paragraph>
                This Agreement shall be governed by and construed in accordance with the laws of the State 
                of Delaware, United States, without regard to its conflict of law provisions.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                15.2 Arbitration
              </Typography>
              <Typography variant="body1" paragraph>
                Any dispute arising out of or relating to this Agreement shall be resolved through binding 
                arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration 
                Association. The arbitration shall be conducted in Delaware, and judgment may be entered 
                in any court of competent jurisdiction.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                15.3 Class Action Waiver
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to resolve disputes with us on an individual basis and waive any right to bring 
                or participate in any class action or representative proceeding.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                16. General Provisions
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.1 Entire Agreement
              </Typography>
              <Typography variant="body1" paragraph>
                This Agreement, together with our Privacy Policy and any applicable purchase orders, constitutes 
                the entire agreement between you and Barrett HTL Inc regarding the Digital Products.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.2 Modifications
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify this Agreement at any time. Changes will be effective upon 
                posting to our website. Your continued use of Digital Products after changes constitutes 
                acceptance of the modified terms.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.3 Severability
              </Typography>
              <Typography variant="body1" paragraph>
                If any provision of this Agreement is held invalid or unenforceable, the remaining provisions 
                shall continue in full force and effect.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.4 Waiver
              </Typography>
              <Typography variant="body1" paragraph>
                No waiver of any term or condition shall be deemed a further or continuing waiver of such 
                term or any other term.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.5 Assignment
              </Typography>
              <Typography variant="body1" paragraph>
                You may not assign or transfer this Agreement or any rights hereunder without our prior 
                written consent. We may assign this Agreement without restriction.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                16.6 Force Majeure
              </Typography>
              <Typography variant="body1" paragraph>
                Neither party shall be liable for any failure or delay in performance due to circumstances 
                beyond its reasonable control, including but not limited to acts of God, natural disasters, 
                war, terrorism, or pandemic.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                17. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about this Agreement or need support, please contact us at:
              </Typography>
              <Box sx={{ pl: 3 }}>
                <Typography variant="body1">
                  <strong>Barrett HTL Inc</strong>
                </Typography>
                <Typography variant="body1">
                  Email: <Link href="mailto:legal@barretthtl.com">legal@barretthtl.com</Link>
                </Typography>
                <Typography variant="body1">
                  Support: <Link href="mailto:support@barretthtl.com">support@barretthtl.com</Link>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 4, p: 3, backgroundColor: 'rgba(1, 30, 65, 0.05)', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                BY PURCHASING OR USING OUR DIGITAL PRODUCTS, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, 
                UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last reviewed: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};